/**
 * Console nền tảng → Gói dịch vụ: tạo / sửa / ngừng bán / xoá gói; mỗi gói gồm module mặc định +
 * hạn mức. Gán gói cho tổ chức ở trang chi tiết tổ chức. Sửa gói áp dụng ngay cho các tổ chức đang dùng
 * (trừ module đã gán tay ở tab Module của tổ chức).
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Lock, Package, Pencil, Plus, Trash2 } from 'lucide-react'
import { platformApi, platformApiError } from '@/lib/platform-client'
import { usePlatformModules } from '@/hooks/use-platform'
import { PageHeader } from '@/components/shared/page-header'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

type KhoaHanMuc = 'nguoiDung' | 'nickZalo' | 'webhook' | 'tenMienRieng'
interface GioiHan { nickZalo: number | null; nguoiDung: number | null; webhook: number | null; tenMienRieng: number | null; anThuongHieuNenTang: boolean }
export interface GoiQuanLy { key: string; ten: string; moTa: string; modules: string[]; gioiHan: GioiHan; isActive: boolean; sortOrder: number; soToChuc: number }

const KHOA = ['platform', 'plans']
const HAN_MUC: Array<{ k: KhoaHanMuc; ten: string }> = [
  { k: 'nguoiDung', ten: 'Người dùng' },
  { k: 'nickZalo', ten: 'Nick Zalo cá nhân' },
  { k: 'webhook', ten: 'Webhook ra ngoài' },
  { k: 'tenMienRieng', ten: 'Tên miền riêng' },
]
const hienSo = (v: number | null) => (v == null ? '∞' : String(v))

function HopThoaiGoi({ goi, onDong }: { goi: GoiQuanLy | 'moi'; onDong: () => void }) {
  const qc = useQueryClient()
  const { data: mods } = usePlatformModules()
  const moi = goi === 'moi'
  const dau = moi ? null : goi
  const [key, setKey] = useState('')
  const [ten, setTen] = useState(dau?.ten ?? '')
  const [moTa, setMoTa] = useState(dau?.moTa ?? '')
  const [modules, setModules] = useState<string[]>(dau?.modules ?? ['customers', 'ai', 'integrations'])
  const [han, setHan] = useState<Record<KhoaHanMuc, string>>({
    nguoiDung: dau?.gioiHan.nguoiDung == null ? '' : String(dau.gioiHan.nguoiDung),
    nickZalo: dau?.gioiHan.nickZalo == null ? '' : String(dau.gioiHan.nickZalo),
    webhook: dau?.gioiHan.webhook == null ? '' : String(dau.gioiHan.webhook),
    tenMienRieng: dau?.gioiHan.tenMienRieng == null ? '' : String(dau.gioiHan.tenMienRieng),
  })
  const [an, setAn] = useState(dau?.gioiHan.anThuongHieuNenTang ?? false)
  const [dangBan, setDangBan] = useState(dau?.isActive ?? true)

  const luu = useMutation({
    mutationFn: async () => {
      const gioiHan = { ...Object.fromEntries(HAN_MUC.map((h) => [h.k, han[h.k].trim() === '' ? null : Number(han[h.k])])), anThuongHieuNenTang: an }
      const body = { ten, moTa, modules, gioiHan, isActive: dangBan }
      return moi ? (await platformApi.post('/platform/plans', { ...body, key })).data : (await platformApi.put(`/platform/plans/${dau!.key}`, body)).data
    },
    onSuccess: (d) => { qc.setQueryData(KHOA, d); void qc.invalidateQueries({ queryKey: ['platform'] }); toast.success(moi ? 'Đã tạo gói' : 'Đã lưu gói — áp dụng ngay cho các tổ chức đang dùng'); onDong() },
    onError: (e) => toast.error(platformApiError(e)),
  })

  // Module gói custom (dữ liệu riêng một khách) không đưa vào gói dịch vụ — gán tay ở tab Module của tổ chức.
  const danhMuc = (mods?.danhMuc ?? []).filter((m) => !m.goiCustom)
  const bat = (k: string, v: boolean) => setModules((xs) => (v ? [...new Set([...xs, k])] : xs.filter((x) => x !== k)))

  return (
    <Dialog open onOpenChange={(o) => !o && onDong()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{moi ? 'Tạo gói dịch vụ' : `Sửa gói ${dau!.ten}`}</DialogTitle>
          <DialogDescription>Module lõi (Tổng quan, Hội thoại, Cài đặt) luôn bật. Hạn mức để trống = không giới hạn.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {moi && <div><Label htmlFor="goi-key" className="text-xs">Mã gói (không đổi được)</Label><Input id="goi-key" value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="vd goi_pro" className="h-9 font-mono" /></div>}
            <div className={moi ? '' : 'sm:col-span-2'}><Label htmlFor="goi-ten" className="text-xs">Tên gói</Label><Input id="goi-ten" value={ten} onChange={(e) => setTen(e.target.value)} placeholder="vd Chuyên nghiệp" className="h-9" /></div>
            <div className="sm:col-span-2"><Label htmlFor="goi-mota" className="text-xs">Mô tả</Label><Input id="goi-mota" value={moTa} onChange={(e) => setMoTa(e.target.value)} placeholder="Gói gồm những gì" className="h-9" /></div>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium">Hạn mức</p>
            <div className="grid gap-2 sm:grid-cols-4">
              {HAN_MUC.map((h) => (
                <div key={h.k}><Label htmlFor={`goi-${h.k}`} className="text-[11px] text-muted-foreground">{h.ten}</Label>
                  <Input id={`goi-${h.k}`} type="number" min={0} value={han[h.k]} onChange={(e) => setHan((x) => ({ ...x, [h.k]: e.target.value }))} placeholder="∞" className="h-9 tabular-nums" /></div>
              ))}
            </div>
            <label htmlFor="goi-an" className="mt-2 flex items-center justify-between rounded-md border p-2.5 text-sm">Được ẩn thương hiệu nền tảng ("Vận hành bởi …") <Switch id="goi-an" checked={an} onCheckedChange={setAn} /></label>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-medium">Module mặc định của gói ({modules.length})</p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {danhMuc.map((m) => (
                <label key={m.key} htmlFor={`goi-m-${m.key}`} className="flex items-start gap-2 rounded-md border p-2 text-sm">
                  <Checkbox id={`goi-m-${m.key}`} checked={m.loi || modules.includes(m.key)} disabled={m.loi} onCheckedChange={(v) => bat(m.key, !!v)} className="mt-0.5" />
                  <span>
                    <span className="inline-flex items-center gap-1 font-medium">{m.ten}{m.loi && <Lock className="h-3 w-3 text-muted-foreground" />}</span>
                    {m.goiCustom && <Badge variant="warning" className="ml-1 px-1 py-0 text-[10px]">gói custom</Badge>}
                    <br /><span className="text-xs text-muted-foreground">{m.moTa}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <label htmlFor="goi-ban" className="flex items-center justify-between rounded-md border p-2.5 text-sm">Đang bán (tắt = ẩn khỏi danh sách chọn gói, tổ chức đang dùng vẫn giữ) <Switch id="goi-ban" checked={dangBan} onCheckedChange={setDangBan} /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onDong}>Huỷ</Button>
          <Button disabled={luu.isPending || !ten.trim() || (moi && key.length < 2)} onClick={() => luu.mutate()}>{luu.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {moi ? 'Tạo gói' : 'Lưu gói'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function PlatformPlansPage() {
  const qc = useQueryClient()
  const { data, isLoading, isError } = useQuery<{ plans: GoiQuanLy[] }>({ queryKey: KHOA, queryFn: async () => (await platformApi.get('/platform/plans')).data })
  const { data: mods } = usePlatformModules()
  const tenModule = useMemo(() => new Map((mods?.danhMuc ?? []).map((m) => [m.key, m.ten])), [mods])
  const [sua, setSua] = useState<GoiQuanLy | 'moi' | null>(null)
  const xoa = useMutation({
    mutationFn: async (key: string) => (await platformApi.delete(`/platform/plans/${key}`)).data,
    onSuccess: (d) => { qc.setQueryData(KHOA, d); toast.success('Đã xoá gói') },
    onError: (e) => toast.error(platformApiError(e)),
  })

  if (isLoading) return <Loading label="Đang tải gói..." />
  if (isError || !data) return <ErrorState message="Không tải được danh sách gói." />

  return (
    <div className="space-y-4">
      <PageHeader
        title="Gói dịch vụ"
        description="Module mặc định và hạn mức theo gói. Gán gói cho tổ chức ở trang chi tiết tổ chức; tổ chức không gán gói = Tuỳ chỉnh (không giới hạn)."
        actions={<Button onClick={() => setSua('moi')}><Plus className="h-4 w-4" /> Tạo gói</Button>}
      />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.plans.map((g) => (
          <div key={g.key} className="flex flex-col gap-3 rounded-lg border bg-card p-4">
            <div className="flex items-start gap-2">
              <Package className="mt-0.5 h-5 w-5 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{g.ten} <code className="text-xs font-normal text-muted-foreground">{g.key}</code></p>
                <p className="text-sm text-muted-foreground">{g.moTa || '—'}</p>
              </div>
              {!g.isActive && <Badge variant="secondary">Ngừng bán</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {HAN_MUC.map((h) => <div key={h.k} className="flex justify-between"><span className="text-muted-foreground">{h.ten}</span><span className="tabular-nums font-medium">{hienSo(g.gioiHan[h.k])}</span></div>)}
              <div className="col-span-2 flex justify-between"><span className="text-muted-foreground">Ẩn thương hiệu nền tảng</span><span className="font-medium">{g.gioiHan.anThuongHieuNenTang ? 'Có' : 'Không'}</span></div>
            </div>
            <div className="flex flex-wrap gap-1">{g.modules.map((k) => <Badge key={k} variant="outline" className="text-[11px]">{tenModule.get(k) ?? k}</Badge>)}</div>
            <div className="mt-auto flex items-center justify-between border-t pt-3">
              <span className="text-sm text-muted-foreground">{g.soToChuc} tổ chức đang dùng</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setSua(g)}><Pencil className="h-3.5 w-3.5" /> Sửa</Button>
                <Button size="sm" variant="ghost" className="text-destructive" disabled={g.soToChuc > 0 || xoa.isPending} title={g.soToChuc > 0 ? 'Còn tổ chức đang dùng — tắt "Đang bán" để ngừng bán' : 'Xoá gói'}
                  onClick={() => { if (confirm(`Xoá gói "${g.ten}"?`)) xoa.mutate(g.key) }}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {sua && <HopThoaiGoi key={sua === 'moi' ? 'moi' : sua.key} goi={sua} onDong={() => setSua(null)} />}
    </div>
  )
}
